import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Form, Input, Modal, Select } from 'antd';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import 'react-quill/dist/quill.snow.css';
import ReactQuill from 'react-quill';
import dayjs from 'dayjs';
import useProjectOptions from '../../hooks/useProjectOptions';
import { FieldBox, FlexWrapper, GreyText, Title } from '../../theme/common_style';
import { createReportApi, getAllEmployees, updateReportApi } from '../../services/api_collection';
import { DropdownIconNew, LmsIcon } from '../../theme/SvgIcons';
import {
  convertToHoursAndMinutes,
  convertToMinutes,
  getFullName
} from '../../utils/common_functions';
import { AvatarSelect } from '../../components/common/AvatarSelect';
import { CreateFormWrapper } from './CreateReport';
import { ReportingType } from '../../utils/constant';
import { useResponsiveModalWidth } from '../../hooks/useResponsiveModalWidth';
import styled from '@emotion/styled';
import isoWeek from 'dayjs/plugin/isoWeek';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isoWeek);
dayjs.extend(isBetween);

const CreateManualReport = ({ open, onCancel, editDetails, handleList }) => {
  const [form] = Form.useForm();
  const { options, loading: projectLoading } = useProjectOptions(form.getFieldValue('emp_id'));
  const [loading, setLoading] = useState(false);
  const quillRef = React.useRef(null);
  const [empLoading, setEmpLoading] = useState(false);
  const [empList, setEmpList] = useState([]);
  const width = useResponsiveModalWidth(800);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      let payload = {
        billable: true,
        project_id: +values?.project_id,
        emp_id: values?.emp_id,
        description: values?.description,
        reporting_type: ReportingType.MANUAL
      };

      payload['billable_mins'] = convertToMinutes(values?.billable_hours, values?.billable_mins);
      payload['reporting_date'] = dayjs(values?.reporting_date).format('YYYY-MM-DD');

      let res;
      if (editDetails) {
        let filteredPayload = { ...payload, id: +editDetails?.reporting_id };
        res = await updateReportApi(filteredPayload);
      } else {
        res = await createReportApi(payload);
      }

      if (res?.statusCode === 200) {
        toast.success('Report added successfully!');
        handleList && handleList();
        onCancel();
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (error) {
      toast.error(error?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleEmpList = async () => {
    try {
      setEmpLoading(true);
      let res = await getAllEmployees();
      if (res?.statusCode === 200) {
        const empData = res?.data?.map((el) => ({
          label: getFullName(el?.first_name, el?.middle_name, el?.last_name),
          value: el?.id
        }));
        setEmpList(empData);
      }
    } catch (error) {
      toast.error(error?.message || 'Something went wrong');
    } finally {
      setEmpLoading(false);
    }
  };

  useEffect(() => {
    handleEmpList();
  }, []);

  useEffect(() => {
    if (editDetails) {
      const time = {};

      if (editDetails?.billable || editDetails?.billable_mins) {
        const { hours, minutes } = convertToHoursAndMinutes(editDetails?.billable_mins);
        time.billable_hours = hours > 0 ? hours : '';
        time.billable_mins = minutes > 0 ? minutes : '';
      } else {
        time.billable_hours = '';
        time.billable_mins = '';
      }

      form.setFieldsValue({
        project_id: editDetails?.project?.id,
        description: editDetails?.description,
        billable: editDetails?.billable,
        reporting_date: dayjs(editDetails?.reporting_date),
        ...time,
        emp_id: editDetails?.employee?.id
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ billable: true });
    }
  }, [editDetails, open]);

  return (
    <Modal
      open={open}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      maskClosable={false}
      centered
      destroyOnClose
      prefixCls="antCustomModal"
      width={width}
      footer={false}>
      <Title style={{ textAlign: 'center' }}>
        {' '}
        {editDetails ? 'Edit Manual Report' : 'Add Manual Report'}
      </Title>
      <Form form={form} onFinish={handleSubmit} initialValues={{ reporting_date: dayjs() }}>
        {() => (
          <>
            <CreateFormWrapper>
              <GridBox cols={2} gap="20px">
                <FieldBox>
                  <label htmlFor="emp_id">
                    Employee <span>*</span>
                  </label>
                  <Form.Item
                    name="emp_id"
                    rules={[
                      {
                        required: true,
                        message: 'Employee is required'
                      }
                    ]}>
                    <AvatarSelect
                      options={empList}
                      placeholder="Select Option"
                      loading={empLoading}
                      isEmp={true}
                      onChange={(val) => {
                        form.setFieldValue('emp_id', val);
                        form.setFieldValue('project_id', null);
                      }}
                    />
                  </Form.Item>
                </FieldBox>
                <FieldBox>
                  <label htmlFor="project_id">
                    Project <span>*</span>
                  </label>
                  <Form.Item
                    name="project_id"
                    rules={[
                      {
                        required: true,
                        message: 'Project is required'
                      }
                    ]}>
                    <Select
                      disabled={!form.getFieldValue('emp_id')}
                      showSearch
                      className="customHeight"
                      allowClear
                      suffixIcon={<DropdownIconNew />}
                      placeholder="Select Option"
                      loading={projectLoading}
                      filterOption={(input, option) =>
                        option.label.toLowerCase().includes(input.toLowerCase())
                      }
                      options={options}
                    />
                  </Form.Item>
                </FieldBox>
              </GridBox>

              <GridBox cols={3}>
                <FieldBox>
                  <label htmlFor="reporting_date">
                    Date <span>*</span>
                  </label>
                  <Form.Item
                    name="reporting_date"
                    type="text"
                    rules={[{ required: true, message: 'Reporting Date is required' }]}>
                    <DatePicker
                      className="customHeight"
                      format="DD MMM, YYYY"
                      placeholder="Select Date"
                      style={{ width: '100%' }}
                      suffixIcon={<LmsIcon />}
                      disabledDate={(current) => {
                        if (!current) return false;

                        const date = dayjs(current);
                        const today = dayjs();

                        const startOfWeek = today.startOf('week').add(1, 'day');
                        const endOfWeek = today.endOf('week');

                        return (
                          !date.isBetween(startOfWeek, endOfWeek, 'day', '[]') ||
                          date.isAfter(today, 'day')
                        );
                      }}
                    />
                  </Form.Item>
                </FieldBox>
                <FieldBox>
                  <label htmlFor="billable_hours">
                    Billing (in Hrs) <span>*</span>
                  </label>
                  <Form.Item
                    name="billable_hours"
                    rules={[
                      ({}) => ({
                        validator(_, value) {
                          if (value === undefined || value === '') {
                            return Promise.reject(new Error('Billing hours are required'));
                          }

                          if (!/^\d+$/.test(value)) {
                            return Promise.reject(new Error('Only numeric values are allowed'));
                          }

                          return Promise.resolve();
                        }
                      })
                    ]}>
                    <Input className="customHeight" placeholder="Enter hours" maxLength={3} />
                  </Form.Item>
                </FieldBox>
                <FieldBox>
                  <label htmlFor="billable_hours">Billing (in Mins)</label>
                  <Form.Item
                    name="billable_mins"
                    rules={[
                      ({}) => ({
                        validator(_, value) {
                          if (value === undefined || value === '') {
                            return Promise.resolve();
                          }
                          if (!/^\d+$/.test(value)) {
                            return Promise.reject(new Error('Only numeric values are allowed'));
                          }
                          const numericValue = parseInt(value, 10);

                          if (numericValue > 59) {
                            return Promise.reject(new Error('Minutes cannot be greater than 59'));
                          }

                          return Promise.resolve();
                        }
                      })
                    ]}>
                    <Input className="customHeight" placeholder="Enter minutes" maxLength={2} />
                  </Form.Item>
                </FieldBox>
              </GridBox>

              <FieldBox>
                <FlexWrapper justify="space-between" width="100%">
                  <label htmlFor="description">
                    Description <span>*</span>
                  </label>
                  <GreyText>Max 300 words</GreyText>
                </FlexWrapper>
                <Form.Item
                  name="description"
                  rules={[
                    {
                      validator: () => {
                        const editor = quillRef.current?.getEditor();
                        const plainText = editor?.getText().trim();
                        const wordCount = plainText
                          ? plainText.split(/\s+/).filter(Boolean).length
                          : 0;

                        if (!plainText) {
                          return Promise.reject(new Error('Description is required'));
                        }

                        if (wordCount > 300) {
                          return Promise.reject(new Error('Description cannot exceed 300 words'));
                        }

                        return Promise.resolve();
                      }
                    }
                  ]}>
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={form.getFieldValue('description')}
                    className="custom-quill"
                    modules={{
                      toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ list: 'ordered' }, { list: 'bullet' }]
                      ]
                    }}
                    onChange={(val) => {
                      const editor = quillRef.current?.getEditor();
                      const plainText = editor?.getText().trim();
                      const wordCount = plainText
                        ? plainText.split(/\s+/).filter(Boolean).length
                        : 0;

                      if (wordCount <= 300) {
                        form.setFieldValue('description', val);
                      }
                    }}
                    placeholder="Write Description here"
                  />
                </Form.Item>
              </FieldBox>

              <FlexWrapper justify="end">
                <Button
                  loading={loading}
                  prefixCls="antCustomBtn"
                  htmlType="submit"
                  style={{ width: '150px', height: '42px' }}>
                  Submit
                </Button>
              </FlexWrapper>
            </CreateFormWrapper>
          </>
        )}
      </Form>
    </Modal>
  );
};

CreateManualReport.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  editDetails: PropTypes.object,
  handleList: PropTypes.func
};

export default CreateManualReport;
export const GridBox = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ cols }) => `repeat(${cols}, 1fr)`};
  gap: ${({ gap }) => gap || '16px'};

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 520px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;
